import { useState, useEffect, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { Stepper as MUIStepper, Step, StepLabel, Snackbar } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSteps, useSelectStep } from 'hooks/steps'
import { isEmpty } from 'lodash'
import FormContext from '../../contexts/FormContext'
import { useCreateEvaluation, useSaveEvaluation } from 'hooks/formsDisplay'
import { Alert } from '@material-ui/lab'
import validateDisplayRules, { validateDisplayRulesCustomer } from 'functions/validateDisplayRules'
import FormReview from '../form-review/FormReview'

const Stepper = ({ steps, customer = {}, send = false, receipt, children }) => {
  const { setActiveStep: setContextStep, setNextStepIsLast } = useContext(FormContext);
  const { selected: stepId } = useSteps()
  const [activeStep, setActiveStep] = useState(0)
  const [skipped, setSkipped] = useState(new Set())
  const [formData, setFormData] = useState({})
  const lastSyncedStepId = useRef(null)
  const selectStep = useSelectStep()
  const classes = useStyles()
  const [state, setSnackbar] = useState({ open: false, severity: 'success' });

  var parentUrl = (window.location != window.parent.location)
    ? document.referrer
    : document.location.href;

  const { open, severity } = state

  const postMessage = () => {
    if (parent && parentUrl)
      parent.postMessage({
        type: "formFinished",
        message: "Formulário finalizado",
        success: true
      }, parentUrl);
  }

  useEffect(() => {
    setContextStep(activeStep)
  }, [activeStep, setContextStep])

  const { mutateAsync: onSubmit, error } = useCreateEvaluation({
    onSuccess: () => {
      window.onbeforeunload = null;
      if (parent && parentUrl) postMessage();
    }
  })

  const { mutateAsync: onSave, errorSave } = useSaveEvaluation({
    onSuccess: () => {
      setSnackbar({ open: true, severity: 'success' });
    }
  })

  useEffect(() => {
    function teste() {
      window.onbeforeunload = function (e) {
        e = e || window.event;
        if (e) {
          e.returnValue = '';
        }
        return '';
      };
    }
    teste()
  }, [])

  const reviewStep = {
    id: 'review-step',
    title: 'Revisão',
    order: Number.MAX_SAFE_INTEGER,
    fields: [],
    display_rules: [],
    isReview: true,
  }

  const stepAux = [...steps, reviewStep]

  useEffect(() => {
    const stepIndex = stepAux.findIndex((s) => s && s.id === stepId)

    if (stepIndex >= 0 && stepId !== lastSyncedStepId.current) {
      setActiveStep(stepIndex)
      lastSyncedStepId.current = stepId
    }
  }, [stepAux, stepId])

  useEffect(() => {
    const step = stepAux[activeStep]
    if (step && step.id && step.id !== 'review-step') {
      selectStep(step.id)
      lastSyncedStepId.current = step.id
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep])

  const isStepOptional = (step) => step === 0

  const isStepSkipped = (step) => skipped.has(step)

  const windowUp = () => {
    window.scroll(0, 0);
  }

  const decimalField = (data) => {
    const fields = [];
    stepAux.map(step => {
      step.fields && step.fields.map(field => fields.push(field))
    })
    const decimalField = fields.find(field => {
      return field.type === 'decimal'
    })
    
    if (!isEmpty(decimalField)) {
      const decimalInput = data[decimalField.id]
      data[decimalField.id] = decimalInput.replace(',', '.');
    }

    return data;
  }

  const imcField = (data) => {
    const fields = [];
    stepAux.map(step => {
      step.fields && step.fields.map(field => fields.push(field))
    })
    const IMC = fields.find(field => {
      return field.type === 'imc' || field.type === 'imc_gestacional'
    })

    if ((!isEmpty(IMC) && !isEmpty(data[IMC.id])) || (data['weight'] && data['height'])) {

      data[IMC.id] = null;

      if (Number(data['weight']) > 0 && Number(data['height']) > 0){
        data[IMC.id] = {
          weight: Number(data['weight']),
          height: Number(data['height']),
        }
      }

      delete data.weight;
      delete data.height;
    }

    return data
  }

  const convertCurrencyMaskedValueToFloat = (data) => {
    stepAux.map(step => {
      step.fields && step.fields.map(field => {
        if (field.mask === 'currency' && data[field.id]) {
          let cleanedString = data[field.id].replace(/[^\d,]/g, '');
          data[field.id] = parseFloat(cleanedString.replace(',', '.'));
        }
      })
    })

    return data
  }

  const handleSave = (data) => {
    if (send)
      onSave(data)
  }

  const stepsToAdvance = (data) => {
    let addSteps = 1;
    const nextStep = stepAux[activeStep + 1]

    // Se o próximo step for o review ou se estivermos no último step normal
    if (!nextStep || nextStep.id === 'review-step' || activeStep === stepAux.length - 2) {
      return 1;
    }

    // Verifica as display rules para os próximos steps
    for (let i = activeStep; i < stepAux.length - 1; i++) {
      const stepToCheck = stepAux[activeStep + addSteps];
      if (stepToCheck && stepToCheck.id !== 'review-step' && 
          (!validateDisplayRules(data, stepToCheck) || !validateDisplayRulesCustomer(customer, stepToCheck))) {
        addSteps += 1;
      } else {
        break;
      }
    }

    return addSteps;
  }

  const handleNext = (data) => {
    // Armazena os dados do formulário se for um objeto válido
    if (data && typeof data === 'object') {
      setFormData(prevData => ({ ...prevData, ...data }))
    }

    // Calcula quantos steps avançar
    const addSteps = stepsToAdvance(data);

    let newSkipped = skipped
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values())
      newSkipped.delete(activeStep)
    }

    const nextStep = Math.min(activeStep + addSteps, stepAux.length - 1);
    
    // Verifica se está no último step E não tem próximo step para mostrar
    const isCurrentlyLastStep = activeStep === stepAux.length - 1;
    const noMoreSteps = nextStep === activeStep; // quando não avança mais

    if (isCurrentlyLastStep && noMoreSteps) {
      const updatedFormData = { ...formData, ...data };
      const processedData = convertCurrencyMaskedValueToFloat(updatedFormData);
      const finalData = decimalField(imcField(processedData));
      
      if (send) {
        console.log('Submetendo dados no último passo (sem mais steps):', finalData);
        onSubmit(finalData);
      }
      
      window.onbeforeunload = null;
    }

    setActiveStep(nextStep);
    setSkipped(newSkipped);
    windowUp();
  }

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(0, prevStep - 1));
    windowUp();
  }

  const handleSkip = (forced) => {
    if (!forced && !isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.")
    }
    windowUp();

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values())
      newSkipped.add(activeStep)
      return newSkipped
    })
  }

  const receiptOrError = () => {
    if (error) {
      return 'Ocorreu um erro ao enviar o formulário'
    }
    return receipt
  }

  return (
    <div className={classes.wrapper}>
      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={open} autoHideDuration={6000} onClose={() => setSnackbar({ ...state, open: false })}>
        <Alert variant="filled" onClose={() => setSnackbar({ ...state, open: false })} severity={severity}>
          {severity === 'success' ?
            'Formulário salvo com sucesso!' :
            'Ocorreu um erro ao salvar.'
          }
        </Alert>
      </Snackbar>
      {stepAux.length > 1 && (
        <MUIStepper activeStep={activeStep}>
          {stepAux.map(({ id, title }) => (
            <Step key={id}>
              <StepLabel/>
            </Step>
          ))}
        </MUIStepper>
      )}

      {(() => {
        if (activeStep === stepAux.length) return receiptOrError()

        if (stepAux[activeStep]?.id === 'review-step') {
          return (
            <FormReview
              steps={stepAux.filter(step => step.id !== 'review-step')}
              formData={formData}
              customer={customer}
              onBack={handleBack}
              onSubmit={() => {
                const finalData = convertCurrencyMaskedValueToFloat(formData)
                onSubmit(decimalField(imcField(finalData)))
              }}
            />
          )
        }

        return children({
          step: stepAux[activeStep],
          handleNext,
          handleBack,
          handleSkip,
          handleSave,
          formData,
        })
      })()}
    </div>
  )
}

const useStyles = makeStyles(() => ({
  wrapper: {
    maxWidth: '450px',
    minWidth: '300px',
    width: '100%',
    paddingLeft: '12px',
    paddingRight: '12px'
  },
}))

Stepper.propTypes = {
  children: PropTypes.func.isRequired,
  customer: PropTypes.object,
  receipt: PropTypes.node.isRequired,
  send: PropTypes.bool.isRequired,
  steps: PropTypes.array.isRequired,
}

export default Stepper
